import { useState, useCallback } from 'react';
import { getConditionKey } from './cutoffUtils';

// Encapsulates deleting a single cutoff from a condition profile: the delete
// confirmation dialog state and the local (staged) removal. Nothing is
// persisted here — the deletion is staged into the working cutoff set and the
// user must click Apply, the single source of truth for saving cutoffs.
const useCutoffDelete = ({
  cutoffValues,
  setCutoffValues,
  setTempConditionRows,
  setBiochemicalError,
}) => {
  const [cutoffDeleteSaving] = useState(false);
  const [cutoffDeleteConfirmOpen, setCutoffDeleteConfirmOpen] = useState(false);
  const [cutoffDeleteTarget, setCutoffDeleteTarget] = useState(null);
  const [deleteAllCutoffsConfirmOpen, setDeleteAllCutoffsConfirmOpen] = useState(false);

  const handleRequestDeleteCutoff = useCallback((condition, cutoffIndex) => {
    setCutoffDeleteTarget({
      conditionKey: getConditionKey(condition),
      cutoffIndex,
    });
    setCutoffDeleteConfirmOpen(true);
  }, []);

  const handleCancelDeleteCutoff = useCallback(() => {
    if (cutoffDeleteSaving) return;
    setCutoffDeleteConfirmOpen(false);
    setCutoffDeleteTarget(null);
  }, [cutoffDeleteSaving]);

  const handleConfirmDeleteCutoff = useCallback(() => {
    if (!cutoffDeleteTarget) return;

    const nextCutoffValues = cutoffValues.map((condition) => {
      if (getConditionKey(condition) !== cutoffDeleteTarget.conditionKey) {
        return condition;
      }

      const conditionProfile = Array.isArray(condition.conditionProfile || condition.ConditionProfile)
        ? [...(condition.conditionProfile || condition.ConditionProfile)]
        : [];

      if (cutoffDeleteTarget.cutoffIndex >= 0 && cutoffDeleteTarget.cutoffIndex < conditionProfile.length) {
        conditionProfile.splice(cutoffDeleteTarget.cutoffIndex, 1);
      }

      // Preserve whichever profile key the condition already uses so the update
      // lands on the key the backend expects and no duplicate field is added.
      const profileKey = condition.conditionProfile !== undefined
        ? 'conditionProfile'
        : (condition.ConditionProfile !== undefined ? 'ConditionProfile' : 'conditionProfile');

      return {
        ...condition,
        [profileKey]: conditionProfile,
      };
    });

    // Stage the removal into the working cutoff set only. Nothing is persisted
    // here — the user must click Apply, the single source of truth for saving
    // cutoffs. initialCutoffValues is intentionally left unchanged so the tab
    // stays dirty until Apply. If this was the condition's last cutoff, the row
    // is hidden automatically (empty-profile conditions are filtered out).
    setBiochemicalError('');
    setCutoffValues(nextCutoffValues);
    setCutoffDeleteConfirmOpen(false);
    setCutoffDeleteTarget(null);
  }, [cutoffDeleteTarget, cutoffValues, setCutoffValues, setBiochemicalError]);

  // --- Delete ALL manual cutoffs (staged) ---
  const handleRequestDeleteAllCutoffs = useCallback(() => {
    setDeleteAllCutoffsConfirmOpen(true);
  }, []);

  const handleCancelDeleteAllCutoffs = useCallback(() => {
    setDeleteAllCutoffsConfirmOpen(false);
  }, []);

  const handleConfirmDeleteAllCutoffs = useCallback(() => {
    // Stage the removal of ALL manual cutoffs only. The default (automatically
    // calculated) cutoffs live in separate state and are never touched here.
    // Nothing is persisted until the user clicks Apply — initialCutoffValues is
    // left unchanged so the tab stays dirty and the unsaved-changes guards keep
    // protecting the page until the empty set is actually saved.
    setBiochemicalError('');
    setCutoffValues([]);
    setTempConditionRows([]);
    setDeleteAllCutoffsConfirmOpen(false);
  }, [setCutoffValues, setTempConditionRows, setBiochemicalError]);

  return {
    cutoffDeleteSaving,
    cutoffDeleteConfirmOpen,
    handleRequestDeleteCutoff,
    handleCancelDeleteCutoff,
    handleConfirmDeleteCutoff,
    deleteAllCutoffsConfirmOpen,
    handleRequestDeleteAllCutoffs,
    handleCancelDeleteAllCutoffs,
    handleConfirmDeleteAllCutoffs,
  };
};

export default useCutoffDelete;
